/**
 * ToggleBlock — details/summary nativ HTML, simplu și accesibil.
 *
 * Summary editabil inline prin prompt() cu buton „editează titlu" în header.
 * Ieșire: Mod-Enter oriunde în bloc sau click pe „↵ ieșire" în header;
 * Enter pe paragraf gol la ultimul child face exit automat.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Node, mergeAttributes, type Editor } from '@tiptap/core';
import type { ResolvedPos } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import {
  NodeViewContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from '@tiptap/react';

export const ToggleBlockNode = Node.create({
  name: 'toggleBlock',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      summary: {
        default: 'Detalii',
        parseHTML: (el) =>
          el.querySelector('summary')?.textContent || 'Detalii',
      },
      open: {
        default: true,
        parseHTML: (el) => el.hasAttribute('open'),
        renderHTML: (attrs) => (attrs.open ? { open: '' } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'details[data-toggle]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const summary = (node.attrs.summary as string) || 'Detalii';
    return [
      'details',
      mergeAttributes(
        {
          'data-toggle': '',
          class:
            'border border-[var(--border-strong)] rounded-md px-3 py-2 my-3',
        },
        HTMLAttributes,
      ),
      [
        'summary',
        {
          class:
            'font-medium cursor-pointer text-[var(--text)]',
        },
        summary,
      ],
      ['div', { class: 'mt-2' }, 0],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ToggleView);
  },

  addKeyboardShortcuts() {
    return {
      'Mod-Enter': () => exitToggleBlock(this.editor),
      Enter: () => {
        const { $from, empty } = this.editor.state.selection;
        if (!empty) return false;
        const para = $from.parent;
        if (para.type.name !== 'paragraph' || para.content.size !== 0) return false;
        const depth = findToggleDepth(this.editor, $from);
        if (depth < 0) return false;
        const parent = $from.node(depth);
        const indexInParent = $from.index(depth);
        if (indexInParent !== parent.childCount - 1) return false;
        const paraStart = $from.before($from.depth);
        const paraEnd = $from.after($from.depth);
        const { tr, schema } = this.editor.state;
        tr.delete(paraStart, paraEnd);
        const insertPos = paraStart;
        tr.insert(insertPos, schema.nodes.paragraph.create());
        tr.setSelection(TextSelection.near(tr.doc.resolve(insertPos + 1)));
        this.editor.view.dispatch(tr);
        return true;
      },
    };
  },
});

function findToggleDepth(_editor: Editor, $from: ResolvedPos): number {
  for (let d = $from.depth; d >= 0; d--) {
    if ($from.node(d).type.name === 'toggleBlock') return d;
  }
  return -1;
}

export function exitToggleBlock(editor: Editor): boolean {
  const { $from } = editor.state.selection;
  const depth = findToggleDepth(editor, $from);
  if (depth < 0) return false;
  const after = $from.after(depth);
  editor
    .chain()
    .focus()
    .insertContentAt(after, { type: 'paragraph' })
    .setTextSelection(after + 1)
    .run();
  return true;
}

function ToggleView({ node, updateAttributes, editor }: NodeViewProps) {
  const { t } = useTranslation();
  const summary = (node.attrs.summary as string) || t('notes_blocks.toggle.default_summary');
  const open = node.attrs.open !== false;
  const editable = editor.isEditable;

  const [editingSummary, setEditingSummary] = useState(false);
  const [draft, setDraft] = useState(summary);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync draft cu summary când node-ul se updatează extern (ex. undo).
  useEffect(() => {
    if (!editingSummary) setDraft(summary);
  }, [summary, editingSummary]);

  useEffect(() => {
    if (editingSummary) {
      const t = window.setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
      return () => window.clearTimeout(t);
    }
  }, [editingSummary]);

  const startEdit = () => {
    if (!editable) return;
    setDraft(summary);
    setEditingSummary(true);
  };

  const commitSummary = () => {
    const next = draft.trim() || t('notes_blocks.toggle.default_summary');
    if (next !== summary) updateAttributes({ summary: next });
    setEditingSummary(false);
  };

  const cancelEdit = () => {
    setDraft(summary);
    setEditingSummary(false);
  };

  return (
    <NodeViewWrapper
      as="div"
      className="border border-[var(--border-strong)] rounded-md px-3 py-2 my-3"
      data-toggle
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          contentEditable={false}
          onClick={() => updateAttributes({ open: !open })}
          className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text)] transition-transform"
          style={{ transform: open ? 'rotate(90deg)' : 'rotate(0)' }}
          title={open ? t('notes_blocks.toggle.collapse') : t('notes_blocks.toggle.expand')}
          aria-expanded={open}
        >
          ▶
        </button>
        {editingSummary && editable ? (
          <input
            ref={inputRef}
            type="text"
            contentEditable={false}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitSummary}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitSummary();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancelEdit();
              }
            }}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 min-w-0 font-medium rounded px-1.5 py-0.5"
            style={{
              background: 'var(--bg-surface)',
              color: 'var(--text)',
              border: '1px solid var(--accent)',
              outline: 'none',
              boxShadow: '0 0 0 3px var(--accent-bg)',
            }}
            placeholder={t('notes_blocks.toggle.default_summary')}
            autoComplete="off"
            spellCheck={false}
          />
        ) : (
          <span
            contentEditable={false}
            className={`flex-1 font-medium text-[var(--text)] select-none truncate ${editable ? 'cursor-text' : ''}`}
            onClick={startEdit}
            title={editable ? t('notes_blocks.toggle.edit_title_hint') : undefined}
          >
            {summary}
          </span>
        )}
        {editable && !editingSummary && (
          <>
            <button
              type="button"
              contentEditable={false}
              onClick={startEdit}
              className="text-[10px] text-[var(--text-muted)] hover:text-[var(--text)]"
              title={t('notes_blocks.toggle.edit_title')}
            >
              ✎
            </button>
            <button
              type="button"
              contentEditable={false}
              onClick={() => exitToggleBlock(editor)}
              className="text-[10px] font-medium text-[var(--text-muted)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)] rounded px-2 py-0.5 border border-[var(--border-strong)]"
              title={t('notes_blocks.toggle.exit_title')}
            >
              {t('notes_blocks.toggle.exit')}
            </button>
          </>
        )}
      </div>
      <div
        className="mt-2"
        style={{ display: open ? 'block' : 'none' }}
      >
        <NodeViewContent />
      </div>
    </NodeViewWrapper>
  );
}
